import Flood_MBTN from 'assets/FloodStageIcons/flood_stage_system.png';
import OVERWATCH_VMS_MBTN from 'assets/icons/exacq.png';
import MANUAL_ROAD_CLOSURE_MBTN from 'assets/map buttons/RescAlert Devices Manual Road Closures - Map Button.png';
import TECHNOLOGY_BRIDGE_SIREN_MBTN from 'assets/map buttons/RescAlert Devices Technology Bridge (Sirens-Giant Voice) - Map button.png';
import TECHNOLOGY_BRIDGE_MBTN from 'assets/map buttons/RescAlert Devices Technology Bridge - Map Button.png';
import FALLBACK from 'assets/resc_placeholder.png';
import CustomLeafletControl from 'components/BaseMap/components/CustomLeafletControl';
import RESCALERT_DEVICE from 'components/Nav/assets/Sidebar_Logo_Closed.png';
import { usePerms } from 'contexts/PermsContext';
import BaseSearchDropdown from 'features/components/BaseSearchDropdown';
import {
  selectLinkingGroupId,
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
} from 'features/Integrations/state/selectors/deviceSelectors';
import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import {
  setLinkingGroupId,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { setVisibleDeviceTypes } from 'features/Integrations/state/slices/integrationSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparisionUtils';
import { canViewDeviceType } from 'features/Integrations/utils/integrationVisibilityUtils';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaList, FaWater } from 'react-icons/fa';
import { GiBigWave } from 'react-icons/gi';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { deviceImageList } from 'utils/devices/icon.utils';
import { isBuoyDevice } from '../../Buoy/utils/buoyDeviceUtils';
import DropdownSelect from '../../DeviceGraph/components/DropdownSelect';
import '../assets/deviceMap.css';

const DeviceFilters = ({
  deviceTypeToFilter,
  selectedGroup,
  setSelectedGroup,
  setUseThisDevice,
  setMapBounds,
  setShowCurrentDevice,
  expandButton = null,
  // The flood sim waterway lines (FloodChainLayer): shown when true; null hides the switch.
  showFloodChain = true,
  setShowFloodChain = null,
}) => {
  // Tracks expanded/collapsed state for the mobile filter panel.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deviceSearchValue, setDeviceSearchValue] = useState('');
  const perms = usePerms();
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const selectedDeviceIndex = useSelector(selectSelectedDeviceIndex);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const linkingGroupId = useSelector(selectLinkingGroupId);
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  const dispatch = useDispatch();
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const hasBuoyDevices = connectedDevices.some((d) => isBuoyDevice(d));

  // Map the incoming filter key to the device_type label stored on devices.
  const deviceTypeToFilterToType = (deviceType) => {
    switch (deviceType) {
      case 'stage':
        return 'Stage';
      case 'manualRoadClosure':
        return 'Manual Road Closure';
      case 'technologyBridge':
        return 'Technology Bridge_Flashing Lights';
      case 'technologyBridgeSiren':
        return 'Technology Bridge_Siren/Giant Voice';
      case 'overwatchVMS':
        return 'Overwatch VMS';
      case 'Monitoring Station':
        return 'Monitoring Station';
      case 'buoy':
        return 'Buoy Conditions';
      default:
        return null;
    }
  };

  // Handles device/group selection and updates map bounds or current device.
  const handleDropdownChange = (e) => {
    if (e.target.value.startsWith('group:')) {
      const groupName = e.target.value.replace('group:', '');
      const group = deviceGroups.find((g) => g.name === groupName);
      setSelectedGroup(group); // Track selected group
      dispatch(setLinkingGroupId(group.id)); // Clears selectedDeviceId via reducer
      // Gather all device IDs from the group
      const allIds = [
        ...(group.devices || []),
        ...(group.controllers || []),
        ...(group.aux_devices || []),
      ];
      // Find all matching devices in nodeList
      const groupDevices = connectedDevices.filter((device) => allIds.includes(device.id));
      // Calculate bounds
      if (groupDevices.length > 0) {
        const lats = groupDevices.map((d) => parseFloat(d.location.lat));
        const lngs = groupDevices.map((d) => parseFloat(d.location.lng));
        const bounds = [
          [Math.min(...lats), Math.min(...lngs)],
          [Math.max(...lats), Math.max(...lngs)],
        ];
        setMapBounds(bounds); // This will zoom the map
      }
      return;
    }
    setSelectedGroup(null); // Clear group selection if a device is selected
    const selectedDevice = connectedDevices[e.target.value];
    dispatch(setSelectedDevice({ index: e.target.value }));
    dispatch(setSelectedDeviceId(selectedDevice?.id ?? null));
    setUseThisDevice(selectedDevice);
    setMapBounds(null);
    const isDatasheetDevice =
      DeviceIsSystemType(selectedDevice, 'Flood') ||
      selectedDevice?.system_type === 'USGS' ||
      isBuoyDevice(selectedDevice);
    if (isDatasheetDevice) {
      setShowCurrentDevice(false);
    } else {
      setShowCurrentDevice(false);
    }
  };

  const getFilterButtonClass = (key, compact) => {
    const selectedClass = visibleDeviceTypes[key] ? ' connected-devices-control-selected' : '';
    return `connected-devices-icon-button device-map-filter-button p-0 d-flex justify-content-center align-items-center${selectedClass}${compact ? ' btn-sm' : ''}`;
  };

  // Shared renderer for the device/group selector; compact toggles mobile sizing.
  const renderDeviceSelect = (compact, searchValue = '', menuOnly = false) => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filteredDevices = connectedDevices
      .map((device, index) => ({ device, originalIndex: index }))
      .filter(({ device }) => {
        if (!deviceTypeToFilter) return true; // If no filter, include all devices
        return (
          device.device_type === deviceTypeToFilterToType(deviceTypeToFilter) &&
          (deviceTypeToFilter !== 'stage' || DeviceIsDeviceType(device, 'Stage')) &&
          canViewDeviceType(device, perms, visibleDeviceTypes)
        );
      })
      .filter(({ device }) => {
        if (!normalizedSearch) return true;
        const searchableValue = `${device.alias || ''} ${device.name || ''} ${
          device.static_id || ''
        } ${device.node_id || ''} ${device.system_type || ''} ${device.device_type || ''}`;
        return searchableValue.toLowerCase().includes(normalizedSearch);
      });

    const filteredGroups = (deviceGroups || []).filter((group) => {
      if (!normalizedSearch) return true;
      return `${group.name || ''} ${group.id || ''}`.toLowerCase().includes(normalizedSearch);
    });

    // Sort by node_id
    const sortedDevices = filteredDevices.sort((a, b) => a.device.node_id - b.device.node_id);

    const groupedBySystem = new Map();

    for (const item of sortedDevices) {
      const system = item.device.system_type || 'Other';

      if (!groupedBySystem.has(system)) {
        groupedBySystem.set(system, []);
      }

      groupedBySystem.get(system).push(item);
    }

    const groupOptions = filteredGroups.map((group) => ({
      label: group.name,
      value: `group:${group.name}`,
      group: 'Device Groups',
    }));

    const deviceOptions = [...groupedBySystem.entries()].flatMap(([systemType, devices]) =>
      devices.map(({ device, originalIndex }) => ({
        label:
          (device.device_type !== 'Manual Road Closure' &&
          device.device_type !== 'Monitoring Station'
            ? `${device.static_id} `
            : '') + device.alias,
        value: `${originalIndex}`,
        group: systemType,
      })),
    );

    const selectedOption =
      linkingGroupId != null && selectedGroup
        ? `group:${selectedGroup.name}`
        : selectedDeviceId != null
          ? `${connectedDevices.findIndex((device) => device.id === selectedDeviceId)}`
          : selectedDeviceIndex != null
            ? `${selectedDeviceIndex}`
            : '';

    return (
      <DropdownSelect
        menuOnly={menuOnly}
        selectedOption={selectedOption === '-1' ? '' : selectedOption}
        options={[...groupOptions, ...deviceOptions]}
        setSelected={() => {}}
        contentStyle={{ maxHeight: 300, overflowY: 'auto', overscrollBehavior: 'contain' }}
        emptyMessage="No devices or groups found."
        placeholder="Select Device"
        triggerStyle={compact ? { minHeight: 31 } : undefined}
        onChange={(event, value) => {
          handleDropdownChange({ target: { value: `${value}` } });
        }}
      />
    );
  };

  // Shared renderer for the device-type filter buttons; compact toggles icon size.
  const renderFilterButtons = (compact) => {
    return (
      <div
        className="w-100 justify-content-end"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 50px)',
          gap: '4px',
        }}
      >
        {/* First row: all main filter buttons */}
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewFloodStageConnections']) && (
          <Button
            className={getFilterButtonClass('Stage', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  Stage: !visibleDeviceTypes['Stage'],
                }),
              );
            }}
            style={{ width: 50, height: 50, gridRow: 1, gridColumn: 1 }}
          >
            <span className="icon-frame" style={{ backgroundImage: `url(${Flood_MBTN})` }} />
          </Button>
        )}
        {/* The flood sim waterway lines (FloodChainLayer): the same square, directly
            under the Stage button (Mike, 2026-08-28). Lit when the lines are shown. */}
        {setShowFloodChain &&
          perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewFloodStageConnections']) && (
            <Button
              className={`connected-devices-icon-button device-map-filter-button p-0 d-flex justify-content-center align-items-center${showFloodChain ? ' connected-devices-control-selected' : ''}${compact ? ' btn-sm' : ''}`}
              onClick={() => setShowFloodChain(!showFloodChain)}
              style={{ width: 50, height: 50, gridRow: 2, gridColumn: 1 }}
              title={`${showFloodChain ? 'Hide' : 'Show'} the waterway lines — the chain, the wave and the plan ring. Hover any of them for the figures. Estimates only.`}
              aria-label="Toggle the waterway lines"
              aria-pressed={!!showFloodChain}
            >
              <FaWater size={26} />
            </Button>
          )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewRescAlertDevicesManualRoadClosureConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Manual Road Closure', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Manual Road Closure': !visibleDeviceTypes['Manual Road Closure'],
                }),
              );
            }}
            style={{ width: 50, height: 50, gridRow: 1, gridColumn: 2 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${MANUAL_ROAD_CLOSURE_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewRescAlertDevicesTechnologyBridge_FlashingLightsConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Technology Bridge_Flashing Lights', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Technology Bridge_Flashing Lights':
                    !visibleDeviceTypes['Technology Bridge_Flashing Lights'],
                }),
              );
            }}
            style={{ width: 50, height: 50, gridRow: 1, gridColumn: 3 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${TECHNOLOGY_BRIDGE_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewRescAlertDevicesTechnologyBridge_Siren-GiantVoiceConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Technology Bridge_Siren/Giant Voice', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Technology Bridge_Siren/Giant Voice':
                    !visibleDeviceTypes['Technology Bridge_Siren/Giant Voice'],
                }),
              );
            }}
            style={{ width: 50, height: 50, gridRow: 1, gridColumn: 4 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${TECHNOLOGY_BRIDGE_SIREN_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewExacqVisionOverwatchVMSConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Overwatch VMS', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Overwatch VMS': !visibleDeviceTypes['Overwatch VMS'],
                }),
              );
            }}
            style={{ width: 50, height: 50, gridRow: 1, gridColumn: 5 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${OVERWATCH_VMS_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewUSGSMonitoringStationConnections']) && (
          <Button
            className={getFilterButtonClass('Monitoring Station', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Monitoring Station': !visibleDeviceTypes['Monitoring Station'],
                }),
              );
            }}
            style={{ width: 50, height: 50, gridRow: 1, gridColumn: 6 }}
          >
            <GiBigWave size={35} />
          </Button>
        )}
        {/* Second row: Buoy Conditions button, aligned to last column */}
        {hasBuoyDevices &&
          perms.testPermission(
            [8, 9, 10, 11, 12, 13],
            ['ViewRescAlertDevicesBuoyConditionsConnections'],
          ) && (
            <Button
              className={getFilterButtonClass('Buoy Conditions', compact)}
              onClick={() => {
                dispatch(
                  setVisibleDeviceTypes({
                    ...visibleDeviceTypes,
                    'Buoy Conditions': !visibleDeviceTypes['Buoy Conditions'],
                  }),
                );
              }}
              style={{ width: 50, height: 50, gridRow: 2, gridColumn: 6 }}
            >
              <span
                className="icon-frame"
                style={{ backgroundImage: `url(${deviceImageList['Buoy Conditions']})` }}
              />
            </Button>
          )}
      </div>
    );
  };

  return (
    <CustomLeafletControl position="topright">
      <div className="d-flex flex-row align-items-start gap-1">
        {expandButton}
        <div
          className="device-filters device-filters-mobile d-flex flex-column align-items-end gap-1"
          style={{ position: 'relative' }}
        >
          <button
            type="button"
            className={`connected-devices-icon-button btn btn-md d-flex align-items-center gap-1${mobileOpen ? ' connected-devices-control-selected' : ''}`}
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            aria-label="Toggle device filters"
          >
            <FaList size={14} />
            Filters
          </button>
          {mobileOpen && (
            <div className="device-filters-panel">
              <BaseSearchDropdown
                className="device-map-selector mb-1"
                searchValue={deviceSearchValue}
                onSearchValueChange={setDeviceSearchValue}
                searchPlaceholder="Search devices"
                searchAriaLabel="Search map devices"
                dropdownContainerClassName="w-100"
                searchDropdownContent={renderDeviceSelect(true, deviceSearchValue, true)}
                dropdownContent={renderDeviceSelect(true)}
              />
              <div className="d-flex flex-wrap justify-content-end gap-1">
                {renderFilterButtons(true)}
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomLeafletControl>
  );
};

export default DeviceFilters;
